/*
  rbk-pdp-gallery.js — Rush PDP mobile gallery enhancers.

  Loaded (defer) ONLY when a Rush gallery option is enabled on the Product
  information section. Everything here READS Fabric's native <media-gallery> /
  slideshow state and drives it through its public API (comp.current /
  comp.select). It does NOT run a parallel slideshow controller (no swipe/scroll
  hijack, no re-implemented paging), so it stays inside the perf budget and
  leaves native analytics + view-transitions untouched.

    WS2  counter pill  — replaces the crowded mobile dots with a "03 / 07" pill.
    WS7  thumbnail rail — transient bottom rail; tap a thumb to jump to a slide.

  IMPORTANT: Horizon marks a WINDOW of slides aria-hidden="false", so aria-hidden
  is NOT the active index — the source of truth is slideshow-component.current.
  aria mutations are used only as the "something changed" signal.

  Rollback: remove the conditional {{ 'rbk-pdp-gallery.js' | asset_url }} load
  and the rbk_g_* settings in sections/product-information.liquid, then delete
  this file. Everything is additive; nothing here fires on a stock Fabric PDP.
*/
(function () {
  'use strict';

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  // Authoritative active-slide index (see header note). Fallbacks keep it
  // working if the component API ever changes under us.
  function readIndex(comp, total) {
    var c = comp.current;
    if (typeof c === 'number' && c >= 0 && c < total) return c;
    var dots = comp.querySelectorAll('.slideshow-controls__dots button, [ref="dots[]"]');
    for (var i = 0; i < dots.length; i++) {
      if (dots[i].getAttribute('aria-selected') === 'true') return i;
    }
    var slides = comp.querySelectorAll('slideshow-slide');
    for (var j = 0; j < slides.length; j++) {
      if (slides[j].getAttribute('aria-hidden') === 'false') return j;
    }
    return 0;
  }

  // Fires cb once per navigation. MutationObserver coalesces the ~20 aria
  // mutations of a single slide change into ONE callback, so rendering directly
  // (no rAF) is both cheap and robust in throttled/non-painting contexts.
  function onSlideChange(comp, cb) {
    var mo = new MutationObserver(cb);
    mo.observe(comp, { attributes: true, attributeFilter: ['aria-hidden', 'aria-selected'], subtree: true });
    return mo;
  }

  /* ----- WS2 counter pill ------------------------------------------------ */
  function initCounter(mg) {
    if (mg.__rbkCounter) return;
    var comp = mg.querySelector('slideshow-component');
    if (!comp) return;
    var total = comp.querySelectorAll('slideshow-slide').length;
    if (total < 2) return; // single media: nothing to count
    mg.__rbkCounter = true;

    if (getComputedStyle(comp).position === 'static') comp.style.position = 'relative';

    var pill = document.createElement('div');
    pill.className = 'rbk-mg-counter';
    pill.setAttribute('aria-hidden', 'true');

    var live = document.createElement('span');
    live.className = 'rbk-mg-counter__live visually-hidden';
    live.setAttribute('aria-live', 'polite');

    comp.appendChild(pill);
    comp.appendChild(live);

    function render() {
      total = comp.querySelectorAll('slideshow-slide').length;
      var active = readIndex(comp, total);
      pill.textContent = pad2(active + 1) + ' / ' + pad2(total);
      live.textContent = 'Image ' + (active + 1) + ' of ' + total;
    }
    render();
    mg.__rbkCounterMo = onSlideChange(comp, render);
  }

  /* ----- WS7 thumbnail rail ---------------------------------------------- */
  // Cheapest thumb URL: the smallest entry Fabric already put in the srcset.
  function thumbURL(slide) {
    var img = slide.querySelector('img');
    if (!img) {
      var vid = slide.querySelector('video');
      return vid && vid.poster ? vid.poster : '';
    }
    var ss = img.getAttribute('srcset');
    if (ss) {
      var best = null, bestW = Infinity;
      ss.split(',').forEach(function (part) {
        var m = part.trim().match(/(\S+)\s+(\d+)w/);
        if (m && +m[2] < bestW) { bestW = +m[2]; best = m[1]; }
      });
      if (best) return best.indexOf('//') === 0 ? 'https:' + best : best;
    }
    return img.currentSrc || img.src;
  }

  function initRail(mg) {
    if (mg.__rbkRail) return;
    var comp = mg.querySelector('slideshow-component');
    if (!comp) return;
    var slides = comp.querySelectorAll('slideshow-slide');
    if (slides.length < 2) return;
    mg.__rbkRail = true;

    if (getComputedStyle(comp).position === 'static') comp.style.position = 'relative';

    var rail = document.createElement('div');
    rail.className = 'rbk-mg-rail';
    rail.hidden = true;
    var track = document.createElement('div');
    track.className = 'rbk-mg-rail__track';
    rail.appendChild(track);

    var thumbs = [];
    Array.prototype.forEach.call(slides, function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rbk-mg-rail__thumb';
      b.setAttribute('aria-label', 'Go to image ' + (i + 1));
      var url = thumbURL(slide);
      if (url) {
        var im = document.createElement('img');
        im.src = url; im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        b.appendChild(im);
      }
      b.addEventListener('click', function () { comp.select(i); show(); arm(); });
      track.appendChild(b);
      thumbs.push(b);
    });
    comp.appendChild(rail);

    function paint() {
      var a = readIndex(comp, thumbs.length);
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].setAttribute('aria-current', i === a ? 'true' : 'false');
      }
      // Only scroll the strip when it is actually on screen, and do it INSTANTLY:
      // the track is `scroll-behavior: smooth`, so a smooth scroll per slide
      // change during a scrub drag is pure jank.
      if (!rail.classList.contains('is-showing')) return;
      var el = thumbs[a];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
    }
    paint();

    // Transient: hidden until the shopper interacts, auto-hides after idle.
    var hideTimer = 0;
    function show() {
      if (rail.hidden) { rail.hidden = false; void rail.offsetWidth; } // forced reflow so the fade runs even when rAF is throttled
      rail.classList.add('is-showing');
    }
    function hide() { rail.classList.remove('is-showing'); }
    // Linger ~5s after the last interaction, then fade — matches the Shella
    // behaviour: thumbnails stay up while the hand is busy, hide once it stops.
    function arm() { clearTimeout(hideTimer); hideTimer = setTimeout(hide, 3000); }

    comp.addEventListener('pointerdown', function () { show(); arm(); });
    comp.addEventListener('touchstart', function () { show(); arm(); }, { passive: true });

    // Let the scrub raise the rail without its events bubbling to the slideshow
    // (bubbling would start a native swipe while you drag the pill).
    mg.__rbkRailPoke = function () { show(); arm(); };

    // Slide changes ONLY repaint the active thumb. They must NOT re-arm the hide
    // timer: Horizon churns aria-hidden on slides while the page scrolls, which
    // would reset the 5s countdown forever and pin the rail open.
    mg.__rbkRailMo = onSlideChange(comp, paint);
  }

  /* ----- 6.2 mobile scrub pill ------------------------------------------ */
  // A thin draggable progress pill. Pill position reflects comp.current/(total-1);
  // dragging maps pointer-x within the track -> nearest slide index -> comp.select(i).
  // Drives NATIVE slideshow state only (no scroll/swipe hijack): the bar is a small
  // element pinned bottom-centre, NOT an overlay across the slides. Bottom-centre so
  // it clears the bottom-LEFT counter, and sits BENEATH the transient rail.
  function initScrub(mg) {
    if (mg.__rbkScrub) return;
    var comp = mg.querySelector('slideshow-component');
    if (!comp) return;
    var total = comp.querySelectorAll('slideshow-slide').length;
    if (total < 2) return; // single media: nothing to scrub
    mg.__rbkScrub = true;

    if (getComputedStyle(comp).position === 'static') comp.style.position = 'relative';

    var wrap = document.createElement('div');
    wrap.className = 'rbk-mg-scrub';
    var track = document.createElement('div');
    track.className = 'rbk-mg-scrub__track';
    var pill = document.createElement('div');
    pill.className = 'rbk-mg-scrub__pill';
    track.appendChild(pill);
    wrap.appendChild(track);

    // Expose as a slider so AT users get position + can page with arrow keys.
    wrap.setAttribute('role', 'slider');
    wrap.setAttribute('aria-label', 'Gallery position');
    wrap.setAttribute('aria-valuemin', '1');
    wrap.setAttribute('tabindex', '0');
    comp.appendChild(wrap);

    var dragging = false;
    var trackRect = null;        // cached per drag: no layout read per pointermove
    var rafId = 0, wantIdx = -1;
    function denom() { return Math.max(1, total - 1); }

    function setP(p) {
      if (p < 0) p = 0; else if (p > 1) p = 1;
      wrap.style.setProperty('--rbk-scrub-p', p);
    }

    // The pill moves by transform, which needs the track width as a number.
    // Measured only on init / grab / resize — never during a move.
    function syncTrackW() {
      var w = (trackRect || track.getBoundingClientRect()).width;
      if (w) wrap.style.setProperty('--rbk-scrub-track-w', w + 'px');
    }

    // Snap to the authoritative discrete index (unless mid-drag). Recomputes
    // total so a variant change that swaps media count stays correct.
    function render() {
      // While dragging the pill is finger-driven; skip so observer work never
      // competes with the drag.
      if (dragging) return;
      syncTrackW();
      total = comp.querySelectorAll('slideshow-slide').length;
      if (total < 2) { wrap.hidden = true; return; }
      wrap.hidden = false;
      var active = readIndex(comp, total);
      wrap.setAttribute('aria-valuemax', String(total));
      wrap.setAttribute('aria-valuenow', String(active + 1));
      wrap.setAttribute('aria-valuetext', 'Image ' + (active + 1) + ' of ' + total);
      setP(active / denom());
    }

    function ratioFromX(clientX) {
      var r = trackRect || track.getBoundingClientRect();
      if (!r.width) return 0;
      var ratio = (clientX - r.left) / r.width;
      return ratio < 0 ? 0 : (ratio > 1 ? 1 : ratio);
    }

    // The gallery follows at most ONCE PER FRAME. comp.select() is expensive
    // (transforms the slideshow + rewrites ~20 aria attrs, waking every
    // observer); calling it straight from pointermove starved the move handler
    // and made the pill visibly trail the finger.
    function commit() {
      rafId = 0;
      if (!dragging) return;
      if (mg.__rbkRailPoke) mg.__rbkRailPoke(); // keep the rail alive, 1x/frame
      if (wantIdx >= 0 && wantIdx !== readIndex(comp, total)) comp.select(wantIdx);
    }

    function onMove(e) {
      if (!dragging) return;
      // Pill is locked to the finger: one CSS-var write. No layout, no select.
      var ratio = ratioFromX(e.clientX);
      setP(ratio);
      wantIdx = Math.round(ratio * denom());
      if (!rafId) rafId = requestAnimationFrame(commit);
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      trackRect = null;
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      wrap.classList.remove('is-dragging');
      try { wrap.releasePointerCapture(e.pointerId); } catch (err) {}
      if (wantIdx >= 0 && wantIdx !== readIndex(comp, total)) comp.select(wantIdx);
      render(); // settle onto the discrete index
    }

    wrap.addEventListener('pointerdown', function (e) {
      // Keep the scrub self-contained: do NOT let this bubble to the rail's
      // pointerdown listener on <slideshow-component> (that would pop the rail
      // and fade the very control being dragged).
      e.stopPropagation();
      e.preventDefault();
      dragging = true;
      trackRect = track.getBoundingClientRect(); // measure once for the whole drag
      syncTrackW();
      wrap.classList.add('is-dragging');
      if (mg.__rbkRailPoke) mg.__rbkRailPoke(); // rail rises ABOVE the scrub
      try { wrap.setPointerCapture(e.pointerId); } catch (err) {}
      var r0 = ratioFromX(e.clientX);
      setP(r0);
      wantIdx = Math.round(r0 * denom());
      if (!rafId) rafId = requestAnimationFrame(commit);
    });
    // Same isolation for the rail's passive touchstart listener on comp.
    wrap.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerup', onUp);
    wrap.addEventListener('pointercancel', onUp);

    // Keyboard: arrows/Home/End page one slide via native select.
    wrap.addEventListener('keydown', function (e) {
      var a = readIndex(comp, total), n = a;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') n = Math.min(total - 1, a + 1);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') n = Math.max(0, a - 1);
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = total - 1;
      else return;
      e.preventDefault();
      if (n !== a) comp.select(n);
    });

    render();
    syncTrackW();
    window.addEventListener('resize', syncTrackW);
    mg.__rbkScrubMo = onSlideChange(comp, render);
  }

  /* ----- boot ------------------------------------------------------------ */
  function boot() {
    var counters = document.querySelectorAll('.rbk-g-counter media-gallery');
    for (var i = 0; i < counters.length; i++) initCounter(counters[i]);
    var rails = document.querySelectorAll('.rbk-g-rail media-gallery');
    for (var j = 0; j < rails.length; j++) initRail(rails[j]);
    var scrubs = document.querySelectorAll('.rbk-g-scrub media-gallery');
    for (var k = 0; k < scrubs.length; k++) initScrub(scrubs[k]);
    watchSwap();
  }

  // Fabric REPLACES the entire <media-gallery> node on a variant change
  // (media-gallery.js -> this.replaceWith(newMediaGallery)), and
  // shopify:section:load does NOT fire for that (it is Theme-Editor-only).
  // Without this, the enhancers silently vanish after a variant swap while the
  // CSS keeps the native dots hidden — a worse-than-stock state. Re-boot on any
  // freshly inserted gallery. The new node has no __rbk* guards, so it re-inits.
  function watchSwap() {
    var hosts = document.querySelectorAll('.product-information__media');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (host.__rbkSwapObs) continue;
      host.__rbkSwapObs = new MutationObserver(function (muts) {
        for (var m = 0; m < muts.length; m++) {
          var added = muts[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var el = added[n];
            if (el.nodeType !== 1) continue;
            if (el.tagName === 'MEDIA-GALLERY' || (el.querySelector && el.querySelector('media-gallery'))) {
              boot();
              return;
            }
          }
        }
      });
      host.__rbkSwapObs.observe(host, { childList: true, subtree: true });
    }
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
